import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Search, 
  ShoppingBag, 
  Gift, 
  MessageSquare, 
  CheckCircle, 
  Star, 
  Shield, 
  Heart,
  ArrowRight,
  Sparkles,
  Users,
  Award,
  Truck,
  CreditCard,
  Eye,
  Hammer,
  Store,
  BadgeCheck,
  ChevronDown,
  MapPin,
  Clock,
  Palette
} from 'lucide-react';

// How it works steps for buyers
const BUYER_STEPS = [
  {
    number: '01',
    title: 'Esplora il Catalogo',
    description: 'Sfoglia le creazioni artigianali autentiche, tutte rigorosamente Made in Italy. Filtra per categoria, stile o artigiano.',
    icon: Search,
    color: 'bg-blue-500'
  },
  {
    number: '02',
    title: 'Scegli il Tuo Pezzo Unico',
    description: 'Ogni prodotto racconta una storia. Leggi la descrizione, guarda le foto e scopri chi è l\'artigiano che lo ha creato.',
    icon: Eye,
    color: 'bg-purple-500'
  },
  {
    number: '03',
    title: 'Acquista in Sicurezza',
    description: 'Pagamenti sicuri con Stripe. Il pagamento viene trattenuto finché non confermi la ricezione del prodotto.',
    icon: CreditCard,
    color: 'bg-green-500'
  },
  {
    number: '04',
    title: 'Ricevi a Casa Tua',
    description: 'Spedizione tracciata direttamente dall\'artigiano. Ogni pacco è un\'esperienza, curato nei minimi dettagli.',
    icon: Truck,
    color: 'bg-amber-500'
  }
];

// How it works steps for custom requests
const CUSTOM_STEPS = [
  {
    number: '01',
    title: 'Descrivi la Tua Idea',
    description: 'Hai in mente qualcosa di unico? Racconta la tua idea, carica immagini di ispirazione e indica il tuo budget.',
    icon: Sparkles,
    color: 'bg-rose-500'
  },
  {
    number: '02',
    title: 'Ricevi Proposte',
    description: 'Gli artigiani interessati ti inviano le loro proposte con prezzo, tempi e dettagli. Senza impegno, valuti con calma.',
    icon: MessageSquare,
    color: 'bg-indigo-500'
  },
  {
    number: '03',
    title: 'Segui la Creazione',
    description: 'Scegli l\'artigiano e segui la lavorazione passo dopo passo. Ricevi foto e aggiornamenti durante tutto il processo.',
    icon: Hammer,
    color: 'bg-teal-500'
  },
  {
    number: '04',
    title: 'Ricevi il Tuo Pezzo Unico',
    description: 'Un oggetto creato solo per te, con la storia della sua realizzazione. Qualcosa che nessun altro al mondo possiede.',
    icon: Gift,
    color: 'bg-pink-500'
  }
];

// Features that make Rarifex unique
const UNIQUE_FEATURES = [
  {
    icon: MapPin,
    title: '100% Made in Italy',
    description: 'Ogni prodotto è realizzato interamente in Italia da artigiani locali. Nessuna eccezione, nessun compromesso.'
  },
  {
    icon: BadgeCheck,
    title: 'Artigiani Verificati',
    description: 'Verifichiamo l\'identità e la partita IVA di ogni artigiano. Gli hobbisti sono chiaramente identificati.'
  },
  {
    icon: Shield,
    title: 'Pagamento Protetto',
    description: 'Il tuo pagamento è trattenuto in sicurezza fino a quando non confermi di aver ricevuto il prodotto.'
  },
  {
    icon: MessageSquare,
    title: 'Comunicazione Diretta',
    description: 'Parla direttamente con chi crea. Nessun intermediario, solo tu e l\'artigiano.'
  },
  {
    icon: Eye,
    title: 'Trasparenza Totale',
    description: 'Vedi chi produce, dove produce, e segui ogni fase della creazione del tuo ordine personalizzato.'
  },
  {
    icon: Heart,
    title: 'Supporti l\'Artigianato',
    description: 'Ogni acquisto sostiene direttamente le botteghe italiane e preserva tradizioni secolari.'
  }
];

// Artisan categories showcase
const ARTISAN_CATEGORIES = [
  {
    name: 'Pelletteria',
    image: 'https://images.pexels.com/photos/4452603/pexels-photo-4452603.jpeg?auto=compress&cs=tinysrgb&w=600',
    description: 'Borse, portafogli e accessori in vera pelle italiana'
  },
  {
    name: 'Ceramica',
    image: 'https://images.unsplash.com/photo-1772487488987-425a4a0c1499?w=600&fit=crop',
    description: 'Piatti, vasi e oggetti decorativi fatti a mano'
  },
  {
    name: 'Gioielleria',
    image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&fit=crop',
    description: 'Gioielli artigianali in oro, argento e pietre preziose'
  },
  {
    name: 'Legno',
    image: 'https://images.unsplash.com/photo-1696628045025-ce19418d9475?w=600&fit=crop',
    description: 'Mobili, oggetti e sculture in legno pregiato'
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: 'Come posso essere sicuro che i prodotti siano davvero Made in Italy?',
    answer: 'Verifichiamo ogni artigiano prima di permettergli di vendere sulla piattaforma. Controlliamo la partita IVA, la sede operativa e la provenienza dei materiali. Gli artigiani sono responsabili della veridicità delle informazioni fornite.'
  },
  {
    question: 'Cosa succede se il prodotto non corrisponde alla descrizione?',
    answer: 'Il pagamento viene trattenuto fino alla conferma di ricezione. Se il prodotto non corrisponde, puoi aprire una contestazione e il nostro team ti assisterà per trovare una soluzione, incluso il rimborso completo se necessario.'
  },
  {
    question: 'Quanto costano i prodotti su Rarifex?',
    answer: 'I prezzi variano molto in base al tipo di lavorazione, ai materiali e al tempo richiesto. Trovi creazioni a partire da poche decine di euro fino a pezzi di alto artigianato. Il prezzo riflette il valore reale del lavoro artigianale.'
  },
  {
    question: 'Posso richiedere personalizzazioni su prodotti esistenti?',
    answer: 'Certamente! Puoi contattare direttamente l\'artigiano per richiedere modifiche o personalizzazioni. Molti artigiani sono felici di adattare le loro creazioni alle tue esigenze.'
  },
  {
    question: 'Come funziona la spedizione?',
    answer: 'Ogni artigiano gestisce le proprie spedizioni. I costi e i tempi variano in base alla località e al tipo di prodotto. Tutti gli ordini sono tracciabili e riceverai aggiornamenti via email e nella tua dashboard.'
  },
  {
    question: 'Qual è la differenza tra artigiani con P.IVA e hobbisti?',
    answer: 'Gli artigiani con partita IVA sono professionisti verificati che emettono fattura. Gli hobbisti sono appassionati che vendono le loro creazioni come attività secondaria. Entrambi garantiscono qualità e autenticità, ma con regimi fiscali diversi.'
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho scoperto artigiani incredibili che non avrei mai trovato altrimenti. Ogni acquisto è un'esperienza unica.",
    author: "Francesca M.",
    location: "Milano",
    rating: 5
  },
  {
    text: "Ho fatto realizzare un regalo personalizzato per mio padre. L'artigiano mi ha tenuto aggiornata con foto durante tutta la lavorazione. Emozione pura.",
    author: "Giulia R.",
    location: "Roma",
    rating: 5
  },
  {
    text: "Finalmente una piattaforma che mette in contatto diretto con chi produce. Niente intermediari, solo autenticità.",
    author: "Marco B.",
    location: "Firenze",
    rating: 5
  }
];

export default function HowItWorksPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Come Funziona Rarifex | Marketplace Artigianato Italiano | Guida Completa</title>
        <meta name="description" content="Scopri come funziona Rarifex: il marketplace dedicato all'artigianato Made in Italy. Acquista prodotti unici, richiedi creazioni personalizzate e scopri artigiani verificati." />
        <meta name="keywords" content="come funziona rarifex, marketplace artigianato italiano, prodotti made in italy, artigiani italiani, regali personalizzati, acquisti sicuri" />
        <link rel="canonical" href="https://www.rarifex.com/come-funziona" />
        <meta property="og:title" content="Come Funziona Rarifex | Marketplace Artigianato Italiano" />
        <meta property="og:description" content="Scopri come acquistare prodotti artigianali Made in Italy e richiedere creazioni personalizzate su Rarifex." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/come-funziona" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#008080]/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Scopri Rarifex
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                L'artigianato italiano a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#008080] to-[#DAA520]">
                  portata di click
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Rarifex è il marketplace dove scopri, acquisti e commissioni creazioni artigianali 
                <strong className="text-slate-800"> esclusivamente Made in Italy</strong>.
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-8 md:gap-12 mt-12">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#008080]">100%</div>
                  <div className="text-sm text-slate-600 mt-1">Made in Italy</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#DAA520]">Zero</div>
                  <div className="text-sm text-slate-600 mt-1">Intermediari</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-slate-800">250+</div>
                  <div className="text-sm text-slate-600 mt-1">Categorie</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="container mx-auto px-4 -mt-8 mb-16">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/5963058/pexels-photo-5963058.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Artigiano italiano al lavoro nella sua bottega"
                className="w-full h-[300px] md:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <p className="text-white text-lg md:text-xl font-medium max-w-2xl">
                  "Dietro ogni creazione c'è una storia, una tradizione, un artigiano che ha dedicato anni 
                  a perfezionare la sua arte."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TWO WAYS TO USE RARIFEX */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Due modi per scoprire l'artigianato italiano
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Che tu voglia acquistare un prodotto pronto o commissionare qualcosa di unico, 
                  Rarifex ti mette in contatto diretto con gli artigiani.
                </p>
              </div>

              {/* MODE 1: SHOP */}
              <div className="mb-20">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-[#008080] rounded-xl flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Acquista dal Catalogo</h3>
                    <p className="text-slate-600">Prodotti pronti, spediti direttamente dall'artigiano</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                  {BUYER_STEPS.map((step, index) => (
                    <div key={index} className="relative">
                      {index < BUYER_STEPS.length - 1 && (
                        <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2 z-0" />
                      )}
                      <Card className="p-6 bg-white border-slate-200 hover:border-[#008080]/30 hover:shadow-lg transition-all relative z-10">
                        <div className={`w-10 h-10 ${step.color} rounded-lg flex items-center justify-center mb-4`}>
                          <step.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-xs font-bold text-slate-400 mb-2">{step.number}</div>
                        <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
                        <p className="text-sm text-slate-600">{step.description}</p>
                      </Card>
                    </div>
                  ))}
                </div>

                <div className="mt-8 text-center">
                  <Link to="/esplora">
                    <Button className="bg-[#008080] hover:bg-[#006666] text-white px-8">
                      Esplora il Catalogo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* MODE 2: CUSTOM */}
              <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-3xl p-8 md:p-12">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-[#DAA520] rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Richiedi una Creazione Personalizzata</h3>
                    <p className="text-slate-600">Descrivi la tua idea e ricevi proposte dagli artigiani</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                  {CUSTOM_STEPS.map((step, index) => (
                    <div key={index} className="relative">
                      {index < CUSTOM_STEPS.length - 1 && (
                        <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-amber-200 -translate-x-1/2 z-0" />
                      )}
                      <Card className="p-6 bg-white/80 backdrop-blur-sm border-amber-200/50 hover:shadow-lg transition-all relative z-10">
                        <div className={`w-10 h-10 ${step.color} rounded-lg flex items-center justify-center mb-4`}>
                          <step.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-xs font-bold text-amber-600 mb-2">{step.number}</div>
                        <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
                        <p className="text-sm text-slate-600">{step.description}</p>
                      </Card>
                    </div>
                  ))}
                </div>

                <div className="mt-8 text-center">
                  <Link to="/regalo-personalizzato">
                    <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8">
                      Crea la Tua Richiesta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT MAKES US UNIQUE */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Cosa rende Rarifex unico
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Non siamo un marketplace qualsiasi. Ogni dettaglio è pensato per valorizzare 
                  l'artigianato italiano e proteggere chi acquista.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {UNIQUE_FEATURES.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex gap-4 p-6 rounded-2xl bg-white border border-slate-100 hover:border-[#008080]/30 hover:shadow-lg transition-all"
                  >
                    <div className="w-12 h-12 bg-[#008080]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#008080]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ARTISAN CATEGORIES SHOWCASE */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Una vetrina per gli artigiani italiani
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Dalla pelletteria toscana alla ceramica di Deruta, dall'oreficeria di Valenza 
                  al vetro di Murano. Su Rarifex trovi il meglio dell'artigianato italiano.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {ARTISAN_CATEGORIES.map((category, index) => (
                  <Link 
                    key={index}
                    to={`/products?category=${category.name}`}
                    className="group relative rounded-2xl overflow-hidden aspect-[4/5]"
                  >
                    <img 
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-bold text-white mb-1">{category.name}</h3>
                      <p className="text-sm text-slate-300">{category.description}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-12 text-center">
                <p className="text-slate-400 mb-4">
                  Sei un artigiano? Unisciti a noi e raggiungi clienti in tutta Italia.
                </p>
                <Link to="/register">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Store className="w-4 h-4 mr-2" />
                    Apri la Tua Vetrina
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Cosa dicono i nostri clienti
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {TESTIMONIALS.map((testimonial, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#008080]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#008080] font-semibold">{testimonial.author[0]}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{testimonial.author}</p>
                        <p className="text-sm text-slate-500">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande Frequenti
                </h2>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#008080] to-[#006666]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Pronto a scoprire l'artigianato italiano?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Inizia a esplorare le creazioni uniche dei nostri artigiani o racconta la tua idea 
                e lascia che gli artigiani ti sorprendano.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/esplora">
                  <Button size="lg" className="bg-white text-[#008080] hover:bg-slate-100">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Esplora il Catalogo
                  </Button>
                </Link>
                <Link to="/regalo-personalizzato">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Gift className="w-5 h-5 mr-2" />
                    Crea una Richiesta
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
